/**
 * Comprehensive Input Validation and Sanitization Module
 * Enforces strict input validation on both client (before dispatch) and server (before execution).
 */

import { ALLOWED_IMAGE_TYPES } from "./sanitize";

export interface ValidationResult<T = unknown> {
  valid: boolean;
  error?: string;
  data?: T;
}

// 1. OCR Image Input Validation
export const MAX_IMAGE_PAYLOAD_BYTES = 10 * 1024 * 1024; // 10 MB
export const ALLOWED_IMAGE_MIMES = ALLOWED_IMAGE_TYPES;

/**
 * Detects the real image or document format by inspecting the initial binary header (magic bytes).
 * Compatible across both Node.js (Buffer) and browser (atob / Uint8Array) environments.
 */
export function detectImageMagicBytes(base64Content: string): string | null {
  try {
    const cleanBase64 = base64Content.replace(/^data:[a-zA-Z0-9.+/-]+;base64,/, "").replace(/\s+/g, "").slice(0, 64);
    let bytes: Uint8Array;

    if (typeof Buffer !== "undefined") {
      bytes = new Uint8Array(Buffer.from(cleanBase64, "base64"));
    } else if (typeof atob === "function") {
      const binaryString = atob(cleanBase64);
      bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
    } else {
      return null;
    }

    if (bytes.length < 4) return null;

    // PDF: %PDF (25 50 44 46)
    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
      return "application/pdf";
    }
    // PNG: 89 50 4E 47
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
      return "image/png";
    }
    // JPEG: FF D8 FF
    if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
      return "image/jpeg";
    }
    // GIF: 47 49 46 38 (GIF87a / GIF89a)
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
      return "image/gif";
    }
    // WebP: RIFF (52 49 46 46) ... WEBP (57 45 42 50)
    if (
      bytes.length >= 12 &&
      bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
      bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
    ) {
      return "image/webp";
    }
    // BMP: 42 4D (BM)
    if (bytes[0] === 0x42 && bytes[1] === 0x4D) {
      return "image/bmp";
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Validates base64 / data-URI image payload before sending to backend or OCR engine.
 * Cross-checks real binary magic bytes against claimed MIME type.
 */
export function validateOcrImageInput(image: unknown): ValidationResult<{ image: string; mimeType: string; rawBase64: string }> {
  if (typeof image !== "string") {
    return { valid: false, error: "Image payload must be a valid base64 data string." };
  }

  const trimmed = image.trim();
  if (trimmed.length === 0) {
    return { valid: false, error: "Image payload is empty. Please select or capture an image." };
  }

  if (trimmed.length > MAX_IMAGE_PAYLOAD_BYTES * 1.4) {
    return {
      valid: false,
      error: `Image payload exceeds the maximum allowed size of 10MB (${(trimmed.length / (1024 * 1024)).toFixed(1)}MB). Please compress or crop the image.`
    };
  }

  let mimeType = "image/png";
  let base64Content = trimmed;

  const dataUriMatch = trimmed.match(/^data:([a-zA-Z0-9.+/-]+);base64,(.+)$/);
  if (dataUriMatch) {
    mimeType = dataUriMatch[1].toLowerCase();
    base64Content = dataUriMatch[2];

    if (!ALLOWED_IMAGE_MIMES.includes(mimeType)) {
      return {
        valid: false,
        error: `Unsupported image MIME type '${mimeType}'. Allowed formats: PNG, JPEG, WebP, GIF, BMP.`
      };
    }
  }

  // Verify Base64 character set
  const cleanBase64 = base64Content.replace(/\s+/g, "");
  if (!/^[A-Za-z0-9+/=]+$/.test(cleanBase64)) {
    return {
      valid: false,
      error: "Malformed base64 image data. Contains invalid characters."
    };
  }

  if (cleanBase64.length < 32) {
    return {
      valid: false,
      error: "Image payload is too small or truncated to be a valid graphic file."
    };
  }

  // Binary magic byte validation
  const detectedMime = detectImageMagicBytes(cleanBase64);
  if (!detectedMime) {
    return {
      valid: false,
      error: "Invalid image binary format. File header is missing a valid PNG, JPEG, WebP, GIF, or BMP byte signature."
    };
  }

  // Cross-check real binary signature against claimed MIME type
  const normalizedClaimed = mimeType.toLowerCase().replace("image/jpg", "image/jpeg");
  if (dataUriMatch && normalizedClaimed !== detectedMime) {
    return {
      valid: false,
      error: `MIME type mismatch: Payload claims '${mimeType}' but image binary signature indicates '${detectedMime}'.`
    };
  }

  return {
    valid: true,
    data: {
      image: trimmed,
      mimeType: detectedMime,
      rawBase64: cleanBase64
    }
  };
}

// 2. Admin Credentials Validation
export interface AdminCredentialsInput {
  username: string;
  password: string;
}

export function validateAdminCredentials(body: unknown): ValidationResult<AdminCredentialsInput> {
  if (!body || typeof body !== "object") {
    return { valid: false, error: "Invalid credentials format." };
  }

  const { username, password } = body as Record<string, unknown>;

  if (typeof username !== "string" || typeof password !== "string") {
    return { valid: false, error: "Username and password must be strings." };
  }

  const cleanUsername = username.trim();
  const cleanPassword = password;

  if (cleanUsername.length === 0 || cleanPassword.length === 0) {
    return { valid: false, error: "Username and password are required." };
  }

  if (cleanUsername.length > 100 || cleanPassword.length > 256) {
    return { valid: false, error: "Credentials exceed permitted character length." };
  }

  return {
    valid: true,
    data: {
      username: cleanUsername,
      password: cleanPassword
    }
  };
}

/**
 * Validates extracted OCR text to ensure it represents real, readable document text
 * and strictly rejects noise/hallucinations generated from non-document images (e.g. photos of people,
 * selfies, objects, clothing textures, or blurry backgrounds).
 */
const COMMON_DOCUMENT_WORDS = new Set([
  "the", "be", "to", "of", "and", "a", "in", "that", "have", "i", "it", "for", "not", "on", "with", 
  "he", "as", "you", "do", "at", "this", "but", "his", "by", "from", "they", "we", "say", "her", 
  "she", "or", "an", "will", "my", "one", "all", "would", "there", "their", "what", "so", "up", 
  "out", "if", "about", "who", "get", "which", "go", "me", "when", "make", "can", "like", "time", 
  "no", "just", "him", "know", "take", "people", "into", "year", "your", "good", "some", "could", 
  "them", "see", "other", "than", "then", "now", "look", "only", "come", "its", "over", "think", 
  "also", "back", "after", "use", "two", "how", "our", "work", "first", "well", "way", "even", 
  "new", "want", "because", "any", "these", "give", "day", "most", "us", "is", "are", "was", "were", 
  "been", "has", "had", "page", "chapter", "section", "title", "name", "date", "subject", "dear", 
  "sincerely", "test", "exam", "student", "school", "university", "report", "essay", "summary", 
  "note", "notes", "paper", "article", "abstract", "conclusion", "result", "results", "study", 
  "data", "method", "review", "book", "text", "paragraph", "line", "number", "word", "words", 
  "letter", "letters", "document", "author", "written", "writing", "read", "reading", "class", 
  "course", "grade", "score", "assignment", "project", "research", "introduction", "analysis", 
  "discussion", "case", "point", "points", "system", "systems", "question", "questions", "answer", 
  "answers", "state", "world", "high", "may", "many", "such", "find", "call", "should", "down", 
  "each", "part", "under", "great", "tell", "group", "problem", "fact", "important", "between", 
  "development", "process", "program", "social", "history", "human", "economic", "information"
]);

export function validateExtractedOcrText(
  rawText: string | null | undefined,
  options?: { confidence?: number; isLocalOcr?: boolean }
): ValidationResult<{ text: string }> {
  if (!rawText || typeof rawText !== "string") {
    return {
      valid: false,
      error: "No readable document text was found in this image. Please retake a clear, well-lit photo of your document."
    };
  }

  const text = rawText.trim();
  
  // Explicit signals of no readable text
  const lower = text.toLowerCase();
  if (
    lower === "[no_readable_text]" ||
    lower.includes("no readable text") ||
    lower.includes("no visible text") ||
    lower.includes("no document text") ||
    lower === "no text found" ||
    lower === "no text" ||
    lower === "[no_text]" ||
    lower.startsWith("i cannot transcribe") ||
    lower.startsWith("there is no text") ||
    lower.startsWith("the image does not contain") ||
    lower.includes("photo of a person") ||
    lower.includes("picture of a person")
  ) {
    return {
      valid: false,
      error: "No readable document text was found in this image. If you uploaded a photo of a person, object, or background, please retake a photo focused squarely on printed or handwritten document text."
    };
  }

  // Tokenize words & tokens
  const rawTokens = text.split(/\s+/).filter(w => w.length > 0);
  if (rawTokens.length === 0) {
    return {
      valid: false,
      error: "No readable document text was found. Please retake a clear photo of the document."
    };
  }

  // Count non-whitespace characters and pure alphanumeric characters
  const nonWhitespaceChars = text.replace(/\s/g, "");
  const alphanumericChars = text.replace(/[^a-zA-Z0-9]/g, "");
  const lettersOnly = text.replace(/[^a-zA-Z]/g, "");

  // Require minimum basic characters
  if (alphanumericChars.length < 3 || (lettersOnly.length < 2 && alphanumericChars.length < 4)) {
    return {
      valid: false,
      error: "No readable document text was detected in this image. If you uploaded a photo of a person, object, or background, please retake a clear photo focused on document text."
    };
  }

  // If this is Local OCR, give higher tolerance to OCR artifact noise on real text
  const isLocal = options?.isLocalOcr === true;

  // Character-level noise ratio (symbols like | \ / ~ < > { } _ = : ;)
  const symbolCount = nonWhitespaceChars.length - alphanumericChars.length;
  const maxSymbolRatio = isLocal ? 0.65 : 0.45;
  if (symbolCount / nonWhitespaceChars.length > maxSymbolRatio && nonWhitespaceChars.length > 20 && !isLocal) {
    return {
      valid: false,
      error: "The scan detected visual image artifacts rather than readable text. Please ensure the camera is aimed directly at a well-lit document."
    };
  }

  // Token analysis: analyze each token for real word vs noise
  let validWordTokens = 0;
  let dictionaryWordTokens = 0;
  let singleCharNoiseTokens = 0;
  let pureSymbolTokens = 0;
  let coherentWordsLength3Plus = 0;

  for (const token of rawTokens) {
    const cleanWord = token.replace(/[^a-zA-Z]/g, "").toLowerCase();
    const isPureSymbol = /^[^a-zA-Z0-9]+$/.test(token);
    
    if (isPureSymbol) {
      pureSymbolTokens++;
      continue;
    }

    if (token.length === 1 || cleanWord.length <= 1) {
      // Single character token
      if (cleanWord === "a" || cleanWord === "i" || /^[0-9]$/.test(token)) {
        validWordTokens += 0.5;
      } else {
        singleCharNoiseTokens++;
      }
      continue;
    }

    // Has dictionary match?
    if (COMMON_DOCUMENT_WORDS.has(cleanWord)) {
      dictionaryWordTokens++;
      validWordTokens++;
      if (cleanWord.length >= 3) coherentWordsLength3Plus++;
      continue;
    }

    // Morphology check for English words (vowels, consonant clusters, or numbers/dates)
    const hasVowel = /[aeiouy]/.test(cleanWord);
    const hasNumber = /[0-9]/.test(token);
    const hasExcessiveRepetition = /(.)\1{3,}/.test(cleanWord);

    if ((cleanWord.length >= 3 && hasVowel && !hasExcessiveRepetition) || hasNumber) {
      validWordTokens++;
      coherentWordsLength3Plus++;
    } else if (cleanWord.length === 2 && hasVowel && !hasExcessiveRepetition) {
      validWordTokens += 0.5;
    } else {
      singleCharNoiseTokens++;
    }
  }

  const noiseTokens = pureSymbolTokens + singleCharNoiseTokens;
  const totalTokens = rawTokens.length;

  // Strict check for noise generated from photos of scenery with zero actual words
  if (!isLocal && totalTokens >= 5 && noiseTokens / totalTokens > 0.60 && coherentWordsLength3Plus === 0) {
    return {
      valid: false,
      error: "No readable document text was detected. The image appears to be a photo of an object rather than a document. Please retake a photo focused on document text."
    };
  }

  if (coherentWordsLength3Plus === 0 && dictionaryWordTokens === 0 && validWordTokens < 1 && !isLocal) {
    return {
      valid: false,
      error: "The image does not contain legible words. Please retake a clear, well-lit photo of your document."
    };
  }

  // If local OCR confidence is provided and extraordinarily low with 0 valid words
  if (isLocal && options?.confidence !== undefined && options.confidence < 25 && coherentWordsLength3Plus === 0 && dictionaryWordTokens === 0) {
    return {
      valid: false,
      error: "The scanned text is too blurry to extract words reliably. Please retake a clear, well-lit photo of your document or use External AI OCR for better efficiency."
    };
  }

  return {
    valid: true,
    data: {
      text
    }
  };
}

/**
 * Transforms any raw error, technical message, stack trace, or HTTP status code
 * into user-focused, plain English with clear instructions on what the user should do next.
 * Eliminates raw JSON strings, URLs, and code snippets from the UI.
 */
export function formatUserFacingError(raw: any, _fallbackContext = "transcription"): string {
  if (!raw) {
    return "We were unable to process this document. Please try uploading the document again or choose another file.";
  }

  let text = typeof raw === "string" ? raw : (raw.message || raw.error || String(raw));

  // If text is a string containing JSON, extract the inner message
  try {
    if (typeof text === "string" && (text.trim().startsWith("{") || text.includes('"message"') || text.includes('"error"'))) {
      const parsed = JSON.parse(text);
      text = parsed?.error?.message || parsed?.error || parsed?.message || text;
    }
  } catch {}

  const lower = String(text).toLowerCase();

  // 1. Quota / daily usage limit
  if (lower.includes("daily") && (lower.includes("quota") || lower.includes("compute") || lower.includes("limit") || lower.includes("allowance") || lower.includes("exceeded"))) {
    return "You have reached today's daily upload allowance. You can continue using the 100% offline Local OCR engine with unlimited scans, or check back tomorrow.";
  }

  // 2. High demand / rate limits / 429 / 503 / 500 / capacity
  if (lower.includes("503") || lower.includes("rate limit") || lower.includes("demand") || lower.includes("unavailable") || lower.includes("resource_exhausted") || lower.includes("429") || lower.includes("overloaded") || lower.includes("busy") || lower.includes("capacity")) {
    return "The online service is temporarily experiencing high demand. Please try again in a few moments, or switch to the Local OCR Engine below for instant processing.";
  }

  // 3. Network / connection issues
  if (lower.includes("network") || lower.includes("fetch failed") || lower.includes("econnrefused") || lower.includes("failed to fetch") || lower.includes("failed to load") || lower.includes("offline")) {
    return "Unable to connect to the server. Please check your internet connection, or switch to the Local Offline OCR engine.";
  }

  // 4. Unclear / empty text detected
  if (lower.includes("no readable text") || lower.includes("unclear") || lower.includes("blurry") || lower.includes("no text detected") || lower.includes("legible") || lower.includes("cannot find text") || lower.includes("no legible words")) {
    return "We couldn't detect readable text in this document. Please make sure the image is well-lit, clearly in focus, and contains printed or handwritten text.";
  }

  // 5. File size / payload limits
  if (lower.includes("file too large") || lower.includes("payload") || lower.includes("size limit") || lower.includes("maximum file size") || lower.includes("413")) {
    return "This file exceeds the maximum supported size. Please choose a document or image under 15MB and try again.";
  }

  // 6. Security / file type / corruption
  if (lower.includes("corrupt") || lower.includes("invalid file") || lower.includes("unsupported file") || lower.includes("mime") || lower.includes("format") || lower.includes("rejected due to security")) {
    return "This file could not be read. Please upload a standard image (PNG, JPG) or PDF document.";
  }

  // 7. Camera hardware / permission
  if (lower.includes("permission") || lower.includes("notallowederror") || lower.includes("camera access") || lower.includes("denied")) {
    return "Camera permission was not granted. Please allow camera access in your browser, or use the 'Upload File / Image' tab.";
  }

  // 8. If text contains technical programming artifacts like code, URLs, brackets, stack traces
  if (
    text.includes("{") ||
    text.includes("}") ||
    text.includes("http://") ||
    text.includes("https://") ||
    text.includes("at ") ||
    text.includes("Error:") ||
    text.includes("TypeError") ||
    text.includes("SyntaxError") ||
    text.includes("GenerativeAI") ||
    text.includes("API_KEY") ||
    text.includes("status code") ||
    /\[\d{3}\]/.test(text)
  ) {
    return "We were unable to process this document with the current settings. Please try uploading the document again, or switch to the Local Offline engine below.";
  }

  return text;
}

/**
 * Canonical word counter across all modules, accounting for punctuation,
 * non-breaking spaces, and empty string trimming.
 */
export function countWords(text: string): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter((w) => w.length > 0).length;
}

